import type { ClubSearchResult, MemberSearchResult } from "@ssf/memdb-api";
import { generatePath } from "./routes.js";

export type SearchMode = "club" | "member";

export interface SearchHit {
  type: SearchMode;
  id: number;
  name: string;
  secondary?: string;
  clubId?: number;
}

export function clubSearchHit(club: ClubSearchResult): SearchHit {
  return {
    type: "club",
    id: club.id,
    name: club.name,
    secondary: [club.schoolName, club.city, club.district?.name]
      .filter(Boolean)
      .join(", "),
  };
}

export function memberSearchHit(member: MemberSearchResult): SearchHit {
  return {
    type: "member",
    id: member.id,
    name: [member.firstName, member.lastName].filter(Boolean).join(" "),
    secondary: member.club?.name || member.city,
    clubId: member.club?.id,
  };
}

export function memberSearchHits(members: MemberSearchResult[]): SearchHit[] {
  return members
    .filter((member) => !!member.club?.id)
    .slice(0, 10)
    .map(memberSearchHit);
}

export function combinedSearchHits(
  clubs: SearchHit[],
  members: SearchHit[],
  query: string,
): SearchHit[] {
  const normalize = (value: string) =>
    value.trim().replace(/\s+/g, " ").toLocaleLowerCase("sv");
  const term = normalize(query);
  const exactClub = clubs.some((hit) => normalize(hit.name) === term);
  const exactMember = members.some((hit) => normalize(hit.name) === term);
  const membersFirst = /^\d+$/.test(term) || (exactMember && !exactClub);
  const clubHits = clubs.slice(0, 10);
  const memberHits = members.filter((hit) => !!hit.clubId).slice(0, 10);
  return membersFirst
    ? [...memberHits, ...clubHits]
    : [...clubHits, ...memberHits];
}

export function searchHitHref(hit: SearchHit): string | undefined {
  if (hit.type === "club") {
    return generatePath("club", { clubId: String(hit.id) });
  }
  if (hit.clubId) {
    return generatePath("member", {
      clubId: String(hit.clubId),
      memberId: String(hit.id),
    });
  }
}

export function rememberSearch(
  hit: SearchHit,
  previous: SearchHit[],
): SearchHit[] {
  return [
    hit,
    ...previous.filter((item) => item.type !== hit.type || item.id !== hit.id),
  ].slice(0, 15);
}
